import React, { FC } from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, TextField, DialogActions, Button, FormControl } from '@mui/material';
import { useMutation } from '@apollo/client';
import { modifyGroupTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

type EditGroupDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    groupToEdit: { _id: string; name: string };
    onEditGroup: (group: { _id: string; name: string }) => void;
};
const EditGroupDialog: FC<EditGroupDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    groupToEdit,
    onEditGroup,
}: EditGroupDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings', 'workflow']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyGroup] = useMutation(modifyGroupTemplate, {
        onCompleted: ({ modifyGroup }) => {
            onEditGroup(modifyGroup);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: { name: string }) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('workflow:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            ...groupToEdit,
        },
        validate,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: async (values) => {
            await modifyGroup({ variables: { ...values } });
            handleDialogClose();
        },
    });

    const handleDialogClose = () => {
        onCloseDialog();
        formik.resetForm();
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={dialogOpen}>
            <DialogTitle>{translate('lab-settings:EDIT_GROUP')}</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="group-name"
                                    margin="dense"
                                    label={translate('lab-settings:GROUP_NAME')}
                                    required
                                    helperText={formik.touched.name && formik.errors.name}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    {...formik.getFieldProps('name')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
                    <Button variant="contained" disabled={!formik.isValid} type="submit">
                        {translate('common:SAVE')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditGroupDialog;
